/** Coach attachments use separate endpoints; they never become task submissions. */
import { useRef, useState, type DragEvent, type ReactNode } from 'react'
import { api } from '@/api/client'
import { putWithProgress } from '@/api/upload'
import { validate } from '@/lib/schema'
import type { CoachAttachment } from '@/lib/types'
import { Icon } from './Icon'
import { MessageComposer } from './MessageComposer'

interface Upload { id: string; file: File; percent: number; busy: boolean; error?: string; uploaded?: CoachAttachment }
interface Props {
  taskId: string
  baseUrl?: string
  disabled: boolean
  onChange: (files: CoachAttachment[], blocked: boolean) => void
  children: ReactNode
  actions: ReactNode
}

export function CoachAttachments({ taskId, baseUrl, disabled, onChange, children, actions }: Props) {
  const [uploads, setUploads] = useState<Upload[]>([])
  const [notice, setNotice] = useState('')
  const current = useRef<Upload[]>([])
  const input = useRef<HTMLInputElement>(null)
  function change(next: Upload[]) {
    current.current = next; setUploads(next)
    onChange(next.flatMap(u => u.uploaded ? [u.uploaded] : []), next.some(u => !u.uploaded))
  }
  function patch(id: string, fields: Partial<Upload>) { change(current.current.map(u => u.id === id ? { ...u, ...fields } : u)) }
  async function upload(item: Upload) {
    patch(item.id, { busy: true, error: undefined })
    try {
      const content_type = item.file.type || 'application/octet-stream'
      const data = { filename: item.file.name, size: item.file.size, content_type }
      const errors = validate('coach_file_register', data)
      if (errors.length) throw new Error('Choose a non-empty file up to 5 GB.')
      const base = baseUrl ? `${baseUrl}/files` : `/sme/tasks/${taskId}/coach/files`
      const registered = await api.post<{ file: CoachAttachment; upload_url: string }>(base, data)
      await putWithProgress(registered.upload_url, item.file, content_type, percent => patch(item.id, { percent }))
      const result = await api.post<{ file: CoachAttachment }>(`${base}/${registered.file.file_id}/complete`)
      patch(item.id, { uploaded: result.file, busy: false })
    } catch (e) { patch(item.id, { busy: false, error: (e as Error).message }) }
  }
  function add(files: File[]) {
    if (disabled) return
    const room = 10 - current.current.length
    setNotice(files.length > room ? 'You can attach up to 10 files per message.' : '')
    const next = files.slice(0, room).map(file => ({ id: crypto.randomUUID(), file, percent: 0, busy: true }))
    change([...current.current, ...next])
    // Avoid competing multi-gigabyte uploads. Each file keeps its own progress and retry state.
    void (async () => { for (const item of next) await upload(item) })()
  }
  function drop(event: DragEvent) { event.preventDefault(); add(Array.from(event.dataTransfer.files)) }
  return <MessageComposer className="coach-attachments" onDragOver={e => e.preventDefault()} onDrop={drop}
    actions={<>
      <button type="button" className="ghost message-icon-button" aria-label="Attach files" title="Attach files" disabled={disabled || uploads.length >= 10} onClick={() => input.current?.click()}><Icon name="paperclip" size={20} /></button>
      {actions}
    </>}>
    {children}
    <input ref={input} type="file" multiple hidden onChange={e => { add(Array.from(e.target.files ?? [])); e.target.value = '' }} />
    {notice && <p role="status" className="small muted">{notice}</p>}
    {uploads.length > 0 && <ul className="coach-attachment-list">
      {uploads.map(u => <li key={u.id}>
        <span className="coach-attachment-name">{u.file.name}</span>
        {u.busy && <span role="status">{u.percent}%</span>}
        {u.error && <><span className="field-error" role="alert">{u.error}</span><button type="button" className="ghost small" disabled={disabled} onClick={() => void upload(u)}>Retry upload</button></>}
        <button type="button" className="ghost small" disabled={disabled || u.busy} aria-label={`Remove ${u.file.name}`} onClick={() => change(current.current.filter(x => x.id !== u.id))}>Remove</button>
      </li>)}
    </ul>}
  </MessageComposer>
}

export function CoachAttachmentLink({ taskId, file, baseUrl }: { taskId: string; baseUrl?: string; file: CoachAttachment }) {
  const [error, setError] = useState('')
  async function download() {
    try {
      const result = await api.get<{ url: string }>(`${baseUrl ?? `/tasks/${taskId}/coach`}/files/${file.file_id}/download`)
      const a = document.createElement('a'); a.href = result.url; a.download = file.filename; a.click()
    } catch { setError('Could not download this attachment. Please retry.') }
  }
  return <span><button type="button" className="ghost small coach-attachment-name" onClick={() => void download()}>{file.filename}</button>{error && <span role="alert" className="field-error">{error}</span>}</span>
}
